import React, {createContext, ReactNode, useCallback, useContext, useState} from 'react';
import {AlertType} from "../types/enums";
import {NodeMenuState} from "../types/menu";
import {Alert, SearchOption} from "../types/Types";

const SETTINGS_KEY = "theCellMapSettings";

interface TheCellMapSettingsType {
  hideNodeLabels: boolean;
}

interface TheCellMapContextType {
  settings: TheCellMapSettingsType;
  setSetting: (setting: keyof TheCellMapSettingsType, value: any) => void;
  loadingCount: number;
  selectedGenes: readonly SearchOption[];
  startLoading: () => void;
  stopLoading: () => void;
  setSelectedGenes: (genes: readonly SearchOption[]) => void;
  currentGene: SearchOption | undefined;
  setCurrentGene: (currentGene: SearchOption | undefined) => void;
  nodeMenuState: NodeMenuState | undefined;
  setNodeMenuState: (s: NodeMenuState | undefined) => void;
  closeNodeMenu: () => void;
  selectedScreen: string | null;
  setSelectedScreen: (screen: string | null) => void;
  alerts: Alert[];
  addAlert: (alert: Alert) => void;
  removeAlert: (alert: Alert) => void;
  clearErrors: () => void;
}

const TheCellMapContext = createContext<TheCellMapContextType | undefined>(undefined);

interface TheCellMapProviderProps {
  children: ReactNode;
}

export const TheCellMapProvider: React.FC<TheCellMapProviderProps> = ({children}) => {
  const [settings, setSettings] = useState<TheCellMapSettingsType>(() => {
    const stored = sessionStorage.getItem(SETTINGS_KEY);
    return stored ? JSON.parse(stored) : {hideNodeLabels: false};
  });

  const [loadingCount, setLoadingCount] = useState(0);
  const [selectedGenes, setSelectedGenes] = useState<readonly SearchOption[]>([]);
  const [currentGene, setCurrentGene] = useState<SearchOption | undefined>(undefined);
  const [nodeMenuState, setNodeMenuState] = useState<NodeMenuState | undefined>(undefined);
  const [selectedScreen, setSelectedScreen] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const startLoading = useCallback(() => setLoadingCount(c => c + 1), []);
  const stopLoading = useCallback(() => setLoadingCount(c => c - 1), []);
  const closeNodeMenu = useCallback(() => setNodeMenuState(undefined), []);

  const addAlert = (alert: Alert) => {
    setAlerts(prev => [...prev, alert]);
  }

  const removeAlert = (alert: Alert) => {
    setAlerts(prev => prev.filter(a => a !== alert));
  }

  const clearErrors = () => {
    setAlerts(prev => prev.filter(alert => alert.type !== AlertType.Error));
  };

  const setSetting = useCallback(<K extends keyof TheCellMapSettingsType>(key: K, value: TheCellMapSettingsType[K]) => {
    setSettings(prev => {
      const updated = {...prev, [key]: value};
      sessionStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <TheCellMapContext.Provider
      value={{
        settings,
        setSetting,
        loadingCount,
        selectedGenes,
        startLoading,
        stopLoading,
        setSelectedGenes,
        currentGene,
        setCurrentGene,
        nodeMenuState,
        setNodeMenuState,
        closeNodeMenu,
        selectedScreen,
        setSelectedScreen,
        alerts,
        addAlert,
        removeAlert,
        clearErrors,
      }}
    >
      {children}
    </TheCellMapContext.Provider>
  );
};

export const useTheCellMapContext = () => {
  const context = useContext(TheCellMapContext);
  if (!context) {
    throw new Error('useTheCellMapContext must be used within an TheCellMapProvider');
  }
  return context;
};
